// API client — 모든 호출이 동일 origin (Vite proxy 또는 동일 서버).

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const detail =
      body && typeof body === 'object' ? (body as Record<string, unknown>) : { raw: body };
    const parts: string[] = [];
    if (typeof detail.error === 'string') parts.push(detail.error);
    if (typeof detail.message === 'string') parts.push(detail.message);
    const summary = parts.length > 0 ? parts.join(' — ') : res.statusText;
    const err = new Error(`HTTP ${res.status}: ${summary}`);
    (err as Error & { status?: number; detail?: unknown }).status = res.status;
    (err as Error & { status?: number; detail?: unknown }).detail = detail;
    throw err;
  }
  return body as T;
}

export const api = {
  get: <T = unknown>(path: string) => request<T>(path),
  post: <T = unknown>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T = unknown>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
};

export interface AdminMe {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

export interface CustomerMe {
  id: string;
  customerId: string;
  email: string;
  displayName: string;
  role: 'owner' | 'member';
}

export interface CustomerRow {
  id: string;
  name: string;
  contactEmail: string | null;
  domain: string | null;
  notes: string | null;
  createdAt: string;
}

export interface LicenseRow {
  id: string;
  customerId: string;
  licenseKey: string;
  type: 'perpetual' | 'subscription';
  expiresAt: string | null;
  maxActivations: number;
  maxUsers: number | null;
  /** 등급 키. null = 사용자 지정(수동 features). */
  level: string | null;
  features: string[];
  revokedAt: string | null;
  notes: string | null;
  createdAt: string;
  activationCount: number;
}

export interface ActivateResponse {
  activationToken: string;
  expiresAt: string | null;
  features: string[];
  level: string | null;
  levelLabel: string | null;
  jti: string;
}

export interface AdminUserRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface CustomerUserRow {
  id: string;
  customerId: string;
  customerName: string | null;
  email: string;
  displayName: string;
  role: 'owner' | 'member';
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export type InquiryCategory = 'product' | 'license';
export type InquiryStatus = 'open' | 'closed';

export interface InquiryRow {
  id: string;
  customerId: string;
  customerName?: string | null;
  openerCustomerUserId: string;
  openerName?: string | null;
  category: InquiryCategory;
  subject: string;
  status: InquiryStatus;
  lastMessageAt: string;
  createdAt: string;
}

export interface InquiryMessageRow {
  id: string;
  inquiryId: string;
  authorType: 'admin' | 'customer';
  authorName: string;
  body: string;
  createdAt: string;
}
